import React from 'react';
import type { ProgramProgressCell } from '../types/domain';
import type { AnnualCrossTopicCountScope } from '../utils/annualProgressCrossTopicExtract';
import { saveAnnualCrossTopicDetailAsExcel } from '../utils/annualCrossTopicDetailExcelExport';
import {
  openDocumentExportPreviewFailureMessage,
  openDocumentExportPreviewInNewTab,
  openDocumentExportPreviewTabPlaceholder,
} from '../utils/documentExportPreviewStorage';
import {
  buildProgramLearningContentDetailRows,
  programLearningContentDetailRowsHaveData,
} from '../utils/programLearningContentDetailExtract';
import type { ProgramExportTrackTimetableParts } from '../utils/programExportDocumentTitle';
import type { ProgramProgressRow } from '../utils/programProgressRows';

export { buildProgramNamedDocumentTitle, buildProgramTrackDocumentTitle } from '../utils/programExportDocumentTitle';
export { resolveProgramExportTimetableName } from '../utils/programExportDocumentTitle';

export function sanitizeProgramExportFileToken(value: string): string {
  return value.trim().replace(/[\\/:*?"<>|]/g, '_').replace(/\s+/g, '_').slice(0, 48);
}

export function buildProgramLearningContentExportFileBase(params: {
  scopeLabel: string;
  programLabel: string;
  termNumber: 1 | 2;
}): string {
  const dateLabel = new Date().toLocaleDateString('ko-KR').replace(/\./g, '').trim();
  const scopePart = sanitizeProgramExportFileToken(params.scopeLabel) || '클래스';
  const programPart = sanitizeProgramExportFileToken(params.programLabel) || '프로그램';
  return `프로그램_${scopePart}_${programPart}_${params.termNumber}학기_${dateLabel}`;
}

export function buildProgramDocumentScopeLabel(termNumber: 1 | 2): string {
  return `${termNumber}학기`;
}

export function useProgramLearningContentDocumentExport(params: {
  tableRows: ProgramProgressRow[];
  programCellsByAnnualKey: Record<string, ProgramProgressCell>;
  scopeLabel: string;
  programTitle: string;
  programTitleParts?: ProgramExportTrackTimetableParts;
  scopeBadge: AnnualCrossTopicCountScope;
  fileNameBase: string;
}): {
  canExport: boolean;
  exportBusy: boolean;
  handleSaveExcel: () => void;
  handleSavePdf: () => void;
  handlePrint: () => void;
} {
  const {
    tableRows,
    programCellsByAnnualKey,
    scopeLabel,
    programTitle,
    programTitleParts,
    scopeBadge,
    fileNameBase,
  } = params;

  const detailRows = React.useMemo(
    () => buildProgramLearningContentDetailRows(tableRows, programCellsByAnnualKey),
    [programCellsByAnnualKey, tableRows]
  );

  const canExport = React.useMemo(
    () => detailRows.length > 0 && programLearningContentDetailRowsHaveData(detailRows),
    [detailRows]
  );

  const [exportBusy, setExportBusy] = React.useState(false);

  const printTitle = React.useMemo(
    () => `${scopeLabel} ${programTitle.replace(/\n/g, ' ')}`,
    [programTitle, scopeLabel]
  );

  const openPreviewTab = React.useCallback(
    (action: 'pdf' | 'print') => {
      if (!canExport) return;
      const placeholder = openDocumentExportPreviewTabPlaceholder();
      const result = openDocumentExportPreviewInNewTab(
        {
          v: 1,
          kind: 'program-learning-content-detail',
          fileNameBase,
          printTitle,
          data: {
            topic: programTitle,
            topicTrackLabel: programTitleParts?.trackLabel,
            topicTimetableLabel: programTitleParts?.timetableLabel,
            scope: scopeBadge,
            scopeLabel,
            rows: detailRows,
          },
        },
        action,
        { preOpenedWindow: placeholder }
      );
      if (!result.ok) {
        alert(openDocumentExportPreviewFailureMessage(result.reason));
      }
    },
    [canExport, detailRows, fileNameBase, printTitle, programTitle, programTitleParts, scopeBadge, scopeLabel]
  );

  const handleSaveExcel = React.useCallback(() => {
    if (!canExport) return;
    setExportBusy(true);
    void saveAnnualCrossTopicDetailAsExcel({
      rows: detailRows,
      scopeLabel,
      topic: programTitle.replace(/\n/g, ' '),
      fileName: `${fileNameBase}.xlsx`,
      sheetName: '프로그램내용',
    })
      .catch((error) => {
        console.error('프로그램 엑셀 저장 실패:', error);
        alert('엑셀 저장에 실패했습니다.');
      })
      .finally(() => {
        setExportBusy(false);
      });
  }, [canExport, detailRows, fileNameBase, programTitle, scopeLabel]);

  const handleSavePdf = React.useCallback(() => {
    openPreviewTab('pdf');
  }, [openPreviewTab]);

  const handlePrint = React.useCallback(() => {
    openPreviewTab('print');
  }, [openPreviewTab]);

  return {
    canExport,
    exportBusy,
    handleSaveExcel,
    handleSavePdf,
    handlePrint,
  };
}
